import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ArticleCard from '@/components/feature/ArticleCard';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ScrollToTop from '@/components/feature/ScrollToTop';
import { categories, articles } from '@/mocks/articles';

export default function Category() {
  const { slug } = useParams<{ slug: string }>();
  const category = categories.find((c) => c.slug === slug);
  const categoryArticles = articles.filter((a) => a.categoryId === category?.id);

  if (!category) {
    return (
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <div className="pt-24 pb-16 text-center">
          <h1 className="font-heading text-2xl text-foreground-900 mb-4">分类未找到</h1>
          <Link to="/" className="text-primary-500 hover:text-primary-600 cursor-pointer whitespace-nowrap">
            返回首页
          </Link>
        </div>
        <Footer />
      </div>
    );
  }

  const pageJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: `${category.name} - 蜜桃网`,
    description: category.description,
    url: `https://mitaowang.com/category/${category.slug}`,
    inLanguage: 'zh-CN',
    isPartOf: {
      '@type': 'WebSite',
      name: '蜜桃网',
      url: 'https://mitaowang.com/',
    },
  };

  return (
    <>
      <ScrollToTop />
      <script type="application/ld+json">
        {JSON.stringify(pageJsonLd)}
      </script>
      <div className="min-h-screen bg-background-50">
        <Navbar />

        {/* Header */}
        <section className="pt-24 pb-10 md:pt-32 md:pb-14 border-b border-background-200/70">
          <div className="w-full max-w-7xl mx-auto px-6 md:px-10">
            <div className="mb-4">
              <Breadcrumb
                items={[
                  { label: '首页', href: '/' },
                  { label: category.name },
                ]}
              />
            </div>
            <div className="flex items-center gap-4 mt-6">
              <div className="w-14 h-14 md:w-16 md:h-16 rounded-2xl bg-primary-100 flex items-center justify-center">
                <i className={`${category.icon} text-2xl md:text-3xl text-primary-600`}></i>
              </div>
              <div>
                <h1 className="font-heading text-2xl md:text-3xl text-foreground-900 mb-1">
                  {category.name}
                </h1>
                <p className="text-foreground-500">{category.description}</p>
              </div>
            </div>
          </div>
        </section>

        {/* Category Tabs */}
        <section className="py-6 border-b border-background-200/70 bg-background-50 sticky top-16 z-40">
          <div className="w-full max-w-7xl mx-auto px-6 md:px-10">
            <div className="flex items-center gap-2 overflow-x-auto pb-1">
              {categories.map((cat) => (
                <Link
                  key={cat.id}
                  to={`/category/${cat.slug}`}
                  className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium transition-all cursor-pointer whitespace-nowrap ${
                    cat.id === category.id
                      ? 'bg-primary-500 text-background-50'
                      : 'bg-background-100 text-foreground-600 hover:text-foreground-900 hover:bg-background-200'
                  }`}
                >
                  <i className={`${cat.icon} text-sm`}></i>
                  {cat.name}
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* Articles Grid */}
        <section className="py-10 md:py-14">
          <div className="w-full max-w-7xl mx-auto px-6 md:px-10">
            {categoryArticles.length > 0 ? (
              <>
                <div className="flex items-center justify-between mb-8">
                  <p className="text-sm text-foreground-500">
                    共 <strong className="text-foreground-800">{categoryArticles.length}</strong> 篇文章
                  </p>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {categoryArticles.map((article) => (
                    <ArticleCard
                      key={article.id}
                      id={article.id}
                      title={article.title}
                      excerpt={article.excerpt}
                      categoryId={article.categoryId}
                      author={article.author}
                      date={article.date}
                      image={article.image}
                      readTime={article.readTime}
                      variant="default"
                    />
                  ))}
                </div>
              </>
            ) : (
              <div className="text-center py-20">
                <div className="w-16 h-16 rounded-2xl bg-background-100 flex items-center justify-center mx-auto mb-4">
                  <i className="ri-file-list-3-line text-2xl text-foreground-300"></i>
                </div>
                <p className="text-foreground-500">该分类下暂无文章</p>
              </div>
            )}
          </div>
        </section>

        <Footer />
      </div>
    </>
  );
}